import { LoginCallback, Security, useOktaAuth } from '@okta/okta-react';
import { toRelativeUrl } from '@okta/okta-auth-js';
import { useEffect } from 'react';
import { BrowserRouter, Route, Routes, useNavigate } from 'react-router-dom';
import Layout from './components/Layout';
import { ToastProvider } from './components/Toast';
import NewRelicIdentity from './nr-identity';
import { getOktaAuth, isOktaConfigured } from './okta-config';
import Chaos from './pages/Chaos';
import Dashboard from './pages/Dashboard';
import Health from './pages/Health';
import Items from './pages/Items';
import Messaging from './pages/Messaging';
import Upload from './pages/Upload';

// Page routes that share the Layout chrome. Rendered as children of either a
// plain <Layout> (anonymous mode) or <SecureRoute><Layout></SecureRoute>
// (Okta mode) so the same tree drives both.
function pageRoutes() {
  return (
    <>
      <Route index element={<Dashboard />} />
      <Route path="items" element={<Items />} />
      <Route path="messaging" element={<Messaging />} />
      <Route path="upload" element={<Upload />} />
      <Route path="chaos" element={<Chaos />} />
      <Route path="health" element={<Health />} />
    </>
  );
}

// Replacement for @okta/okta-react's <SecureRoute>, which only works with
// react-router-dom v5 (it calls v5's useMatch under the hood and throws
// "Unsupported: SecureRoute only works with react-router-dom v5" against
// our v6 router). Same contract: render children when authenticated, kick
// unauthenticated users into signInWithRedirect() and remember where they
// were trying to go so restoreOriginalUri can land them back there.
//
// Refresh-handling note: on page reload the SDK's authState transitions
// null -> { isAuthenticated: false } -> { isAuthenticated: true } as it
// hydrates tokens from localStorage. Acting on the transient false state
// would re-bounce the user to Okta on every refresh. The await on
// oktaAuth.isAuthenticated() below collapses that race -- the async method
// waits for the SDK to finish reading + validating stored tokens before
// answering, so we only redirect when truly unauthenticated.
function RequireAuth({ children }) {
  const { authState, oktaAuth } = useOktaAuth();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const authed = await oktaAuth.isAuthenticated();
      if (cancelled) return;
      if (authed) return;
      const originalUri = toRelativeUrl(
        window.location.href,
        window.location.origin,
      );
      oktaAuth.setOriginalUri(originalUri);
      oktaAuth.signInWithRedirect();
    })();
    return () => {
      cancelled = true;
    };
  }, [oktaAuth]);

  if (!authState || !authState.isAuthenticated) {
    return (
      <div style={{ padding: '2rem', color: '#64748b', fontSize: '0.9rem' }}>
        Loading...
      </div>
    );
  }
  return children;
}

function SecuredApp() {
  const navigate = useNavigate();
  // Use the module-level singleton so api/client.js's request interceptor
  // and the <Security> provider share the same token cache and silent-renewal
  // loop. Constructing a second instance here would race the singleton.
  const oktaAuth = getOktaAuth();

  // toRelativeUrl strips the origin so we don't accidentally bounce users to a
  // different host after login \u2014 important when the same bundle runs on
  // localhost, *.sro-team-test.nikecloud.com, etc.
  const restoreOriginalUri = (_oktaAuth, originalUri) => {
    navigate(toRelativeUrl(originalUri || '/', window.location.origin), {
      replace: true,
    });
  };

  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      <NewRelicIdentity />
      <Routes>
        {/* Path matches OktaConfig.redirectUri in okta-config.js, which in
            turn matches what's allow-listed on nike.intexp.pal. */}
        <Route path="/pla/login" element={<LoginCallback />} />
        <Route
          element={
            <RequireAuth>
              <Layout />
            </RequireAuth>
          }
        >
          {pageRoutes()}
        </Route>
      </Routes>
    </Security>
  );
}

function AnonymousApp() {
  return (
    <Routes>
      <Route element={<Layout />}>{pageRoutes()}</Route>
    </Routes>
  );
}

// react-router-dom v6 nags about two upcoming v7 behaviors at startup unless
// we opt in. Both are no-ops for this app: v7_startTransition just wraps
// state updates in React.startTransition (we have no synchronous-update
// dependencies), and v7_relativeSplatPath only affects splat (*) routes
// (we have none). Setting them silences the console without changing behavior.
const ROUTER_FUTURE_FLAGS = {
  v7_startTransition: true,
  v7_relativeSplatPath: true,
};

export default function App() {
  return (
    <ToastProvider>
      <BrowserRouter future={ROUTER_FUTURE_FLAGS}>
        {isOktaConfigured() ? <SecuredApp /> : <AnonymousApp />}
      </BrowserRouter>
    </ToastProvider>
  );
}
